import { useCallback, useEffect, useRef, useState } from 'react';

import {
  mapCaptureErrorToReason,
  resolveCapturePlan,
  type CaptureOutcome,
  type PhotoCapturer,
} from './captureReason';

/**
 * The DOM edge of photo capture (plan-v7). Keeps a persistent front-facing
 * `getUserMedia` stream WARM on the clock screen while photo-on-punch is enabled,
 * and exposes a {@link PhotoCapturer} that grabs ONE frame at PIN submit, draws it
 * to an offscreen canvas sized by the pure `resolveCapturePlan`, and encodes a
 * JPEG. All decision logic (reason mapping, downscale plan) lives in the pure
 * `captureReason.ts`; this hook only wires it to the browser.
 *
 * ALLOW + FLAG (Decision 3): the capturer NEVER rejects — any failure resolves to
 * a `missing` {@link CaptureOutcome}, so the punch that awaits it is never blocked.
 */

export interface CameraController {
  /** Attach to the warm-preview `<video>` element on the clock screen. */
  videoRef: React.RefObject<HTMLVideoElement>;
  /** True once the stream is live and a frame can be grabbed. */
  ready: boolean;
  /** Grab one downscaled JPEG frame; resolves `missing` on any failure. */
  capture: PhotoCapturer;
}

function nowIso(): string {
  return new Date().toISOString();
}

function missing(error: unknown): CaptureOutcome {
  return {
    status: 'missing',
    blob: null,
    missingReason: mapCaptureErrorToReason(error),
    capturedAt: nowIso(),
  };
}

/** Promise wrapper over the callback-based `canvas.toBlob`. */
function canvasToJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob === null) {
          reject(new Error('canvas.toBlob returned null'));
        } else {
          resolve(blob);
        }
      },
      'image/jpeg',
      quality
    );
  });
}

export function useCamera(enabled: boolean): CameraController {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;

    async function warmUp(): Promise<void> {
      try {
        const media = navigator.mediaDevices;
        if (media === undefined) {
          return; // no camera API in this context — capture() will flag no_camera
        }
        const stream = await media.getUserMedia({ video: { facingMode: 'user' } });
        if (cancelled) {
          for (const track of stream.getTracks()) {
            track.stop();
          }
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video !== null) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        setReady(true);
      } catch {
        // Permission denied / no device: leave ready=false. capture() maps the
        // reason itself when invoked, so nothing here blocks the clock.
        setReady(false);
      }
    }

    void warmUp();

    return () => {
      cancelled = true;
      setReady(false);
      if (streamRef.current !== null) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
        streamRef.current = null;
      }
    };
  }, [enabled]);

  const capture = useCallback<PhotoCapturer>(async () => {
    try {
      const video = videoRef.current;
      const stream = streamRef.current;
      if (video === null || stream === null || !stream.active) {
        return missing({ name: 'NotFoundError' });
      }
      const frameWidth = video.videoWidth;
      const frameHeight = video.videoHeight;
      if (frameWidth <= 0 || frameHeight <= 0) {
        return missing({ name: 'CaptureFailed' });
      }
      const plan = resolveCapturePlan(frameWidth, frameHeight);
      const canvas = document.createElement('canvas');
      canvas.width = plan.targetW;
      canvas.height = plan.targetH;
      const ctx = canvas.getContext('2d');
      if (ctx === null) {
        return missing({ name: 'CaptureFailed' });
      }
      ctx.drawImage(video, 0, 0, plan.targetW, plan.targetH);
      const blob = await canvasToJpeg(canvas, plan.quality);
      return { status: 'stored', blob, capturedAt: nowIso() };
    } catch (error) {
      return missing(error);
    }
  }, []);

  return { videoRef, ready, capture };
}
